/**
 * Stellar Arbitrage Monitor
 * 
 * Detects arbitrage opportunities between Stellar DEX and spot prices.
 * Also monitors cross-chain arbitrage (XRPL <-> Stellar).
 */

import * as StellarSdk from '@stellar/stellar-sdk';
import type { Grams } from './types/Weight';
import { displayGrams } from './types/Weight';

export interface StellarOrderBookSnapshot {
  readonly bids: Array<{ price: number; amount: number }>;
  readonly asks: Array<{ price: number; amount: number }>;
  readonly timestamp: Date;
}

export interface StellarArbitrageOpportunity {
  readonly type: 'mint-sell' | 'buy-burn';
  readonly dexPrice: number;
  readonly spotPrice: number;
  readonly profitBps: number;
  readonly profitPct: number;
  readonly size: number;
  readonly grossProfit: number;
  readonly timestamp: Date;
}

export interface CrossChainArbitrageOpportunity {
  readonly type: 'stellar-to-xrpl' | 'xrpl-to-stellar';
  readonly stellarPrice: number;
  readonly xrplPrice: number;
  readonly profitBps: number;
  readonly profitPct: number;
  readonly size: number;
  readonly grossProfit: number;
  readonly timestamp: Date;
}

/**
 * Stellar Arbitrage Monitor
 */
export class StellarArbitrageMonitor {
  private server: StellarSdk.Horizon.Server;
  private assetCode: string;
  private issuerPublicKey: string;
  private baseAsset: 'XLM' | 'USDC' | 'USDT';
  private baseIssuer?: string;
  
  constructor(
    assetCode: string,
    issuerPublicKey: string,
    baseAsset: 'XLM' | 'USDC' | 'USDT',
    baseIssuer?: string,
    horizonUrl: string = 'https://horizon.stellar.org'
  ) {
    this.assetCode = assetCode;
    this.issuerPublicKey = issuerPublicKey;
    this.baseAsset = baseAsset;
    this.baseIssuer = baseIssuer;
    this.server = new StellarSdk.Horizon.Server(horizonUrl);
  }
  
  /**
   * Get order book from Stellar DEX
   */
  async getOrderBook(): Promise<StellarOrderBookSnapshot> {
    const asset = new StellarSdk.Asset(this.assetCode, this.issuerPublicKey);
    const baseAsset = this.baseAsset === 'XLM'
      ? StellarSdk.Asset.native()
      : new StellarSdk.Asset(this.baseAsset, this.baseIssuer!);
    
    const orderbook = await this.server
      .orderbook(asset, baseAsset)
      .limit(20)
      .call();
    
    return {
      bids: orderbook.bids.map(bid => ({
        price: parseFloat(bid.price),
        amount: parseFloat(bid.amount)
      })),
      asks: orderbook.asks.map(ask => ({
        price: parseFloat(ask.price),
        amount: parseFloat(ask.amount)
      })),
      timestamp: new Date()
    };
  }
  
  /**
   * Check for arbitrage opportunities
   */
  async checkArbitrage(
    spotPricePerGram: number,
    gramsPerToken: Grams,
    minProfitBps: number = 100
  ): Promise<StellarArbitrageOpportunity | null> {
    const orderBook = await this.getOrderBook();
    
    if (orderBook.bids.length === 0 || orderBook.asks.length === 0) {
      return null;
    }
    
    const fairValue = spotPricePerGram * displayGrams(gramsPerToken);
    const bestBid = orderBook.bids[0].price;
    const bestAsk = orderBook.asks[0].price;
    
    // Mint-sell opportunity (DEX bid > spot)
    if (bestBid > fairValue * (1 + minProfitBps / 10000)) {
      const profitPct = ((bestBid - fairValue) / fairValue) * 100;
      const profitBps = Math.floor(profitPct * 100);
      const size = Math.min(orderBook.bids[0].amount, 1000);
      const grossProfit = (bestBid - fairValue) * size;
      
      return {
        type: 'mint-sell',
        dexPrice: bestBid,
        spotPrice: fairValue,
        profitBps,
        profitPct,
        size,
        grossProfit,
        timestamp: new Date()
      };
    }
    
    // Buy-burn opportunity (DEX ask < spot)
    if (bestAsk < fairValue * (1 - minProfitBps / 10000)) {
      const profitPct = ((fairValue - bestAsk) / bestAsk) * 100;
      const profitBps = Math.floor(profitPct * 100);
      const size = Math.min(orderBook.asks[0].amount, 1000);
      const grossProfit = (fairValue - bestAsk) * size;
      
      return {
        type: 'buy-burn',
        dexPrice: bestAsk,
        spotPrice: fairValue,
        profitBps,
        profitPct,
        size,
        grossProfit,
        timestamp: new Date()
      };
    }
    
    return null;
  }
  
  /**
   * Check cross-chain arbitrage (Stellar vs XRPL)
   */
  async checkCrossChainArbitrage(
    xrplPrice: number,
    minProfitBps: number = 100
  ): Promise<CrossChainArbitrageOpportunity | null> {
    const orderBook = await this.getOrderBook();
    
    if (orderBook.bids.length === 0 || orderBook.asks.length === 0) {
      return null;
    }
    
    const stellarBestBid = orderBook.bids[0].price;
    const stellarBestAsk = orderBook.asks[0].price;
    
    // Buy on Stellar, sell on XRPL
    if (xrplPrice > stellarBestAsk * (1 + minProfitBps / 10000)) {
      const profitPct = ((xrplPrice - stellarBestAsk) / stellarBestAsk) * 100;
      const profitBps = Math.floor(profitPct * 100);
      const size = Math.min(orderBook.asks[0].amount, 1000);
      const grossProfit = (xrplPrice - stellarBestAsk) * size;
      
      return {
        type: 'stellar-to-xrpl',
        stellarPrice: stellarBestAsk,
        xrplPrice,
        profitBps,
        profitPct,
        size,
        grossProfit,
        timestamp: new Date()
      };
    }
    
    // Buy on XRPL, sell on Stellar
    if (stellarBestBid > xrplPrice * (1 + minProfitBps / 10000)) {
      const profitPct = ((stellarBestBid - xrplPrice) / xrplPrice) * 100;
      const profitBps = Math.floor(profitPct * 100);
      const size = Math.min(orderBook.bids[0].amount, 1000);
      const grossProfit = (stellarBestBid - xrplPrice) * size;
      
      return {
        type: 'xrpl-to-stellar',
        stellarPrice: stellarBestBid,
        xrplPrice,
        profitBps,
        profitPct,
        size,
        grossProfit,
        timestamp: new Date()
      };
    }
    
    return null;
  }
  
  /**
   * Calculate profit from opportunity
   */
  calculateProfit(
    opportunity: StellarArbitrageOpportunity,
    gasCost: number = 0.01
  ): number {
    return opportunity.grossProfit - gasCost;
  }
  
  /**
   * Start monitoring loop
   */
  async startMonitoring(
    spotPricePerGram: number,
    gramsPerToken: Grams,
    xrplPrice: number | null,
    onOpportunity: (opp: StellarArbitrageOpportunity | CrossChainArbitrageOpportunity) => void,
    intervalMs: number = 15000
  ): Promise<() => void> {
    const interval = setInterval(async () => {
      try {
        // Check single-chain arbitrage
        const singleChainOpp = await this.checkArbitrage(spotPricePerGram, gramsPerToken);
        if (singleChainOpp) {
          onOpportunity(singleChainOpp);
        }
        
        // Check cross-chain arbitrage
        if (xrplPrice !== null) {
          const crossChainOpp = await this.checkCrossChainArbitrage(xrplPrice);
          if (crossChainOpp) {
            onOpportunity(crossChainOpp);
          }
        }
      } catch (error) {
        console.error('Stellar arbitrage monitoring error:', error);
      }
    }, intervalMs);
    
    return () => clearInterval(interval);
  }
  
  /**
   * Get trade statistics
   */
  async getTradeStats(limit: number = 100): Promise<{
    recentTrades: number;
    avgPrice: number;
    volume24h: number;
  }> {
    const asset = new StellarSdk.Asset(this.assetCode, this.issuerPublicKey);
    const baseAsset = this.baseAsset === 'XLM'
      ? StellarSdk.Asset.native()
      : new StellarSdk.Asset(this.baseAsset, this.baseIssuer!);
    
    try {
      const trades = await this.server
        .trades()
        .forAssetPair(asset, baseAsset)
        .limit(limit)
        .order('desc')
        .call();
      
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      const recentTrades = trades.records.filter(
        t => new Date(t.ledger_close_time) > oneDayAgo
      );
      
      const volume24h = recentTrades.reduce((sum, t) => {
        return sum + parseFloat(t.base_amount);
      }, 0);
      
      const avgPrice = recentTrades.length > 0
        ? recentTrades.reduce((sum, t) => {
            if (!t.price) return sum;
            const priceN = parseFloat(String(t.price.n));
            const priceD = parseFloat(String(t.price.d));
            return sum + priceN / priceD;
          }, 0) / recentTrades.length
        : 0;
      
      return {
        recentTrades: recentTrades.length,
        avgPrice,
        volume24h
      };
    } catch (error) {
      return {
        recentTrades: 0,
        avgPrice: 0,
        volume24h: 0
      };
    }
  }
}

/**
 * Create monitor for AURG on Stellar
 */
export function createAURGStellarMonitor(
  issuerPublicKey: string,
  baseAsset: 'XLM' | 'USDC' | 'USDT' = 'XLM',
  baseIssuer?: string
): StellarArbitrageMonitor {
  return new StellarArbitrageMonitor('AURG', issuerPublicKey, baseAsset, baseIssuer);
}
